import { Directive, HostBinding, HostListener, Input, OnChanges } from '@angular/core';
import { NgModel, NG_VALIDATORS, AbstractControl, Validator, ValidationErrors, UntypedFormControl, ValidatorFn } from '@angular/forms';
import { Observable } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { CreditCardValidators } from '../shared/creditcardvalidators';

@Directive({
  selector: '[required]',
  providers: [{ provide: NG_VALIDATORS, useExisting: RequiredValidator, multi: true }]
})
export class RequiredValidator implements Validator {


  validate(control: AbstractControl): ValidationErrors | null {

    const value = control.value;
    let valid;

    if (typeof value == 'boolean')
      valid = value;
    else
      valid = !/^\s+$/.test(value);

    return valid ? null : { required: true };
  }
}

@Directive({
  selector: '[appExactMatch]',
  providers: [{ provide: NG_VALIDATORS, useExisting: ExactMatchValidator, multi: true }]
})
export class ExactMatchValidator implements Validator {
  @Input() appExactMatch!: NgModel;

  validate(control: AbstractControl): ValidationErrors | null {
    const valid = control.value == this.appExactMatch.value;
    return valid ? null : { appExactMatch: true };
  }
}

@Directive({
  selector: '[appCheckClash]',
  providers: [{ provide: NG_VALIDATORS, useExisting: CheckClashValidator, multi: true }]
})
export class CheckClashValidator implements Validator {
  @Input() appCheckClash!: Observable<boolean>;
  @HostBinding('class.focused') focused = false;

  @Input() maxAttempts?: number;
  @Input() lockoutTime?: number;
  public attempts = 0;

  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.control)
      this.control = control;
    return this.focused ? { changed: true } : null;
  }

  @HostListener('focus', ['$event']) onFocus() {
    this.focused = true;
  }
  @HostListener('blur', ['$event']) onblur() {
    this.focused = false;

    const keys = Object.keys(this.control?.errors || { changed: true });
    if (keys.length > 1 || keys[0] != 'changed')
      return;

    const lock = () => {
      this.control?.disable();
      this.control?.setErrors({ ...this.control?.errors, lockedOut: true });

      window.setTimeout(() => {
        this.attempts = 0;
        this.control?.enable();
        check();
      }, this.lockoutTime || 60000);
    };

    const check = () => {
      this.attempts++;

      const errors = { ...this.control?.errors, pending: true };
      this.control?.setErrors(errors);

      this.appCheckClash.subscribe(clash => {
        const errors = this.control?.errors || {};

        delete errors.changed;
        delete errors.pending;
        delete errors.lockedOut;
        if (clash)
          errors.clash = true;

        this.control?.setErrors(Object.keys(errors).length == 0 ? null : errors);
      }, () => {
        lock();
      });
    };

    if (this.maxAttempts == null || this.attempts < this.maxAttempts) {
      check();
    } else {
      lock();
    }
  }
}

@Directive({
  selector: '[appUrlValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: UrlValidator, multi: true }]
})
export class UrlValidator implements Validator {

  validate(control: AbstractControl): ValidationErrors | null {
    // const regex = /(?=^.{4,253}$)(^((?!-)[a-zA-Z0-9-]{1,63}(?<!-)\.)+[a-zA-Z]{2,63}$)/;
    const regex = new RegExp(
      "^" +
      // protocol identifier (optional)
      // short syntax // still required
      "(?:(?:(?:https?|ftp):)?\\/\\/)?" +
      // user:pass BasicAuth (optional)
      "(?:\\S+(?::\\S*)?@)?" +
      "(?:" +
      // IP address exclusion
      // private & local networks
      "(?!(?:10|127)(?:\\.\\d{1,3}){3})" +
      "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" +
      "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})" +
      // IP address dotted notation octets
      // excludes loopback network 0.0.0.0
      // excludes reserved space >= 224.0.0.0
      // excludes network & broadcast addresses
      // (first & last IP address of each class)
      "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" +
      "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" +
      "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" +
      "|" +
      // host & domain names, may end with dot
      // can be replaced by a shortest alternative
      // (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
      "(?:" +
      "(?:" +
      "[a-z0-9\\u00a1-\\uffff]" +
      "[a-z0-9\\u00a1-\\uffff_-]{0,62}" +
      ")?" +
      "[a-z0-9\\u00a1-\\uffff]\\." +
      ")+" +
      // TLD identifier name, may end with dot
      "(?:[a-z\\u00a1-\\uffff]{2,}\\.?)" +
      ")" +
      // port number (optional)
      "(?::\\d{2,5})?" +
      // resource path (optional)
      "(?:[/?#]\\S*)?" +
      "$", "i"
    );
    const valid = regex.test(control.value) || !control.value;

    return valid ? null : { invalidUrl: true };
  }
}

@Directive({
  selector: '[appZipValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: ZipValidatorDirective, multi: true }]
})
export class ZipValidatorDirective implements Validator, OnChanges {
  @Input() country?: string;
  @HostBinding('attr.required') required?: string;

  public isValid !: boolean;
  public regex !: RegExp;
  validator: ValidatorFn;
  constructor() {
    this.validator = this.zipValidator();
  }
  validate(c: UntypedFormControl) {
    return this.validator(c);
  }
  zipValidator(): ValidatorFn {
    return (c) => {
      if (c.value === '') {
        return null;
      }

      switch (this.country) {
        case 'us':
          this.regex = /^(\d{5}(-\d{4})?)?$/;
          this.isValid = this.regex.test(c.value);
          break;
        case 'ca':
          this.regex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
          this.isValid = this.regex.test(c.value);
          break;
        default:
          this.isValid = true;
      }

      if (this.isValid) {
        return null;
      } else {
        return {
          zipValidator: {
            valid: this.isValid,
            country: this.country,
            regex: this.regex,
            value: c.value
          }
        };
      }
    };
  }
  ngOnChanges() {
    this.required = this.country == 'us' || this.country == 'ca' ? 'required' : undefined;
  }
}

@Directive({
  selector: '[appNoSpecialCharValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: NoSpecialCharValidatorDirective, multi: true }]
})
export class NoSpecialCharValidatorDirective implements Validator {
  public isValid !: boolean;
  public regex !: RegExp;
  validator: ValidatorFn;
  constructor() {
    this.validator = this.appNoSpecialCharValidator();
  }
  validate(c: UntypedFormControl) {
    return this.validator(c);
  }
  appNoSpecialCharValidator(): ValidatorFn {
    return (c) => {
      if (c.value === '') {
        return null;
      }

      this.regex = /^[a-zA-Z]*((-|\s)*[a-zA-Z])*[a-zA-Z]$/;
      this.isValid = this.regex.test(c.value);

      if (this.isValid) {
        return null;
      } else {
        return {
          appNoSpecialCharValidator: {
            valid: this.isValid,
            regex: this.regex,
            value: c.value
          }
        };
      }
    };
  }
}


@Directive({
  selector: '[appFirstNameValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: FirstNameValidatorDirective, multi: true }]
})
export class FirstNameValidatorDirective implements Validator {
  public isValid !: boolean;
  public regex !: RegExp;
  validator: ValidatorFn;
  constructor() {
    this.validator = this.fct();
  }
  validate(c: UntypedFormControl) {
    return this.validator(c);
  }
  public fct(): ValidatorFn {
    return (c) => {
      if (c.value === '') {
        return null;
      }

      // not confident here but going with it.
      this.regex = /^[a-zA-Z]+((-|\.|\s)*[a-zA-Z]*)*[\s|.|'|a-zA-Z]$/;
      this.isValid = this.regex.test(c.value);

      if (this.isValid) {
        return null;
      } else {
        return {
          appFirstNameValidator: {
            valid: this.isValid,
            regex: this.regex,
            value: c.value
          }
        };
      }
    };
  }
}


@Directive({
  selector: '[appLastNameValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: LastNameValidatorDirective, multi: true }]
})
export class LastNameValidatorDirective implements Validator {
  public isValid !: boolean;
  public regex !: RegExp;
  validator: ValidatorFn;
  constructor() {
    this.validator = this.fct();
  }
  validate(c: UntypedFormControl) {
    return this.validator(c);
  }
  public fct(): ValidatorFn {
    return (c) => {
      if (c.value === '') {
        return null;
      }

      // not confident here but going with it.
      this.regex = /^[a-zA-Z]+((-|'|\s)*[a-zA-Z]*)*[\s|'|a-zA-Z]$/;
      this.isValid = this.regex.test(c.value);

      if (this.isValid) {
        return null;
      } else {
        return {
          appLastNameValidator: {
            valid: this.isValid,
            regex: this.regex,
            value: c.value
          }
        };
      }
    };
  }
}

@Directive({
  selector: '[appEmailValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: EmailValidatorDirective, multi: true }]
})
export class EmailValidatorDirective implements Validator {
  @HostBinding('type') type = 'email';

  validate(control: AbstractControl): ValidationErrors | null {
    const valid = /^\w+([-+.'`!#$%^&*]\w+)*@\w+([-.]\w+)*\.\w\w+$/.test(control.value) || !control.value;

    return valid ? null : { email: true };
  }
}

@Directive({
  selector: '[appPublicDomainEmailValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: PublicDomainEmailValidatorDirective, multi: true }]
})
export class PublicDomainEmailValidatorDirective implements Validator {
  @Input() publicEmailDomains: string[] | undefined;
  @HostBinding('type') type = 'email';

  validate(control: AbstractControl): ValidationErrors | null {
    const domains = this.publicEmailDomains;
    const valid = /^\w+([-+.'`!#$%^&*]\w+)*@\w+([-.]\w+)*\.\w\w+$/.test(control.value) || !control.value;
    if (valid) {
      const invalid = control.value && domains && domains.some(val => control.value?.toLowerCase().includes(val?.toLowerCase()));
      return invalid ? { emailDomain: true } : null;
    }
    return { email: true };
  }
}


@Directive({
  selector: '[appAlphaNumOnly]'
})
export class AlphaNumOnlyDirective {

  @HostListener('keydown', ["$event"]) keyEvent(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return true;
    }
    const valid = /^[a-zA-Z0-9]+$/.test(event.key);

    if (valid) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

}

@Directive({
  selector: '[appShortCodeValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: ShortCodeValidatorDirective, multi: true }]
})
export class ShortCodeValidatorDirective implements Validator {
  public isValid !: boolean;
  public regex !: RegExp;
  validator: ValidatorFn;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input('appShortCodeValidator') options: any;

  constructor() {
    this.validator = this.appShortCodeValidator();
  }
  validate(c: UntypedFormControl) {
    return this.validator(c);
  }
  appShortCodeValidator(): ValidatorFn {
    return (c) => {

      if (this.options && !this.options.apply) {
        return null;
      }

      if (c.value === '') {
        return null;
      }

      this.regex = /^(?!(?:.*\*){3})[A-Za-z2-9*][A-Za-z\d*]{4,5}$/;
      this.isValid = this.regex.test(c.value);

      if (this.isValid) {
        return null;
      } else {
        return {
          appShortCodeValidator: {
            valid: this.isValid,
            regex: this.regex,
            value: c.value
          }
        };
      }
    };
  }
}

@Directive({
  selector: '[appShortCodeInput]'
})
export class ShortCodeInputDirective {
  @Input() shortCode!: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input('appShortCodeInput') options: any;

  @HostListener('keydown', ["$event"]) keyEvent(event: KeyboardEvent) {
    if (this.options && !this.options.apply) {
      return;
    }

    if (!this.shortCode) {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return true;
    }

    let valid = /^[a-zA-Z0-9*]+$/.test(event.key);

    if (!(this.shortCode.split('*').length - 1 < 2)) {
      valid = /^[a-zA-Z0-9]+$/.test(event.key);
    }

    if (valid) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

}

@Directive({
  selector: '[appCvvValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: CvvValidator, multi: true }]
})
export class CvvValidator implements Validator, OnChanges {
  @Input() cardType: string | undefined;
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;

    const type = this.cardType;
    const val = control.value;

    if (!val)
      return null;

    if (type == 'Amex' && val.length != 4)
      return { cvvLengthAmex: true };

    if (type != 'Amex' && val.length != 3)
      return { cvvLength: true };

    return null;
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appCcValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: CcValidator, multi: true }]
})
export class CcValidator implements Validator, OnChanges {
  @Input() cardType!: string | undefined;
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;

    const type = this.cardType;
    const val = control.value;

    if (!val)
      return null;

    if (type == 'Amex')
      if (val.length != 15)
        return { ccLength: '15' };
      else if (!/^3[47]/.test(val))
        return { cardNumTypeMismatch: true };

    if (type == 'Discover')
      if (val.length != 16)
        return { ccLength: '16' };
      else if (!/^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)/.test(val))
        return { cardNumTypeMismatch: true };

    if (type == 'Mastercard')
      if (val.length != 16)
        return { ccLength: '16' };
      else if (!/^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/.test(val))
        return { cardNumTypeMismatch: true };

    if (type == 'Visa')
      if (val.length != 13 && val.length != 16)
        return { ccLength: '13 or 16' };
      else if (!/^4/.test(val))
        return { cardNumTypeMismatch: true };

    return CreditCardValidators.validateCCNumber(control);
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appCcExpirationValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: CcExpirationValidator, multi: true }]
})
export class CcExpirationValidator implements Validator, OnChanges {
  @Input() month?: number;
  @Input() year?: number;
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;

    let expirationMonth, expirationYear;
    if (this.year) {
      expirationMonth = +control.value;
      expirationYear = this.year;
    } else {
      expirationMonth = this.month;
      expirationYear = +control.value;
    }

    if (!expirationMonth || !expirationYear)
      return null;

    const today = new Date();

    const month = today.getMonth() + 1;
    const year = today.getFullYear();

    const yyyymm = (month: number, year: number): number => {
      const mm = `${month}`.padStart(2, '0');
      return +`${year}${mm}`;
    };

    return yyyymm(month, year) > yyyymm(expirationMonth, expirationYear) ?
      this.month ? { cardExpirationYear: true } : { cardExpiration: true } : null;
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}


@Directive({
  selector: '[appFecIdValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: FecIdValidator, multi: true }]
})
export class FecIdValidator implements Validator, OnChanges {
  @Input() required = false;
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;

    const valid = /[CHSP][A-Z0-9]{8}/.test(control.value) || !control.value;

    return valid ? null : { invalidFecId: true };
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appNoBlankSpaces]',
  providers: [{ provide: NG_VALIDATORS, useExisting: NoBlankSpacesValidator, multi: true }]
})
export class NoBlankSpacesValidator implements Validator, OnChanges {
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    const valid = /\S/.test(control.value);
    return valid ? null : { noBlankSpaces: true };
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}



@Directive({
  selector: '[appNoLeadingTrailingSpaces]',
  providers: [{ provide: NG_VALIDATORS, useExisting: NoLeadingTrailingSpacesValidator, multi: true }]
})
export class NoLeadingTrailingSpacesValidator implements Validator, OnChanges {
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    if (!control.value) {
      return null;
    }
    const trimmed = control.value.trim();
    if (trimmed.length !== control.value.length) {
      return { noLeadingTrailingSpaces: true };
    }
    return null;
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appAmountValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: AmountValidator, multi: true }]
})
export class AmountValidator implements Validator, OnChanges {
  control?: AbstractControl;
  @Input() min!: string;
  @Input() max!: string;
  amountPattern = /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    let error = null;
    const pattern = this.amountPattern.test(control.value);
    if (!pattern)
      error = { invalidAmount: true };
    else {
      const min = parseFloat(this.min);
      const max = parseFloat(this.max);
      const value = parseFloat(control.value);
      console.log(min, max, value);
      if (min && (min > value)) {
        error = { min: true };
      }
      if (max && (max < value)) {
        error = { max: true };
      }
    }
    return error;
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appRangeValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: RangeValidator, multi: true }]
})
export class RangeValidator implements Validator, OnChanges {
  control?: AbstractControl;
  @Input() min: string | number | undefined;
  @Input() max: string | number | undefined;
  @Input() appRangeValidator?: boolean | string;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    let error = null;
    if (this.appRangeValidator === 'false' || this.appRangeValidator === false) {
      return error;
    }
    const min = (typeof this.min === 'string') ? parseFloat(this.min) : this.min;
    const max = (typeof this.max === 'string') ? parseFloat(this.max) : this.max;
    const value = parseFloat(control.value);
    if (min && (min > value)) {
      error = { min: true };
    }
    if (max && (max < value)) {
      error = { max: true };
    }

    return error;
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appLessComparisonValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: LessComparisonValidator, multi: true }]
})
export class LessComparisonValidator implements Validator, OnChanges {
  control?: AbstractControl;
  @Input() comparisonField!: NgModel;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    const comparedValue = Number(this.comparisonField.value);
    const value = Number(control.value);
    if (value < comparedValue) {
      return {
        comparisonValidator: { valueLarger: false, comparedValue, value }
      };
    } else {
      if (this.comparisonField.invalid) {
        this.comparisonField.control.updateValueAndValidity();
      }
      return null;
    }
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appGreaterComparisonValidator]',
  providers: [{ provide: NG_VALIDATORS, useExisting: GreaterComparisonValidator, multi: true }]
})
export class GreaterComparisonValidator implements Validator, OnChanges {
  control?: AbstractControl;
  @Input() comparisonField!: NgModel;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    const comparedValue = Number(this.comparisonField.value);
    const value = Number(control.value);
    if (value > comparedValue) {
      return {
        comparisonValidator: { valueSmaller: false, comparedValue, value }
      };
    } else {
      if (this.comparisonField.invalid) {
        this.comparisonField.control.updateValueAndValidity();
      }
      return null;
    }
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

@Directive({
  selector: '[appCheckSum]',
  providers: [{ provide: NG_VALIDATORS, useExisting: CheckSumValidator, multi: true }]
})
export class CheckSumValidator implements Validator, OnChanges {
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    if (control.value) {
      const v = control.value.split('').map((c: string) => parseInt(c));
      const sum = 3*(v[0] + v[3] + v[6]) + 7*(v[1]+v[4]+v[7]) + v[2] + v[5] + v[8];
      if (sum % 10 !== 0) {
        return { checksumValidator: { failed: true } };
      }
    }
    return null;
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}



@Directive({
  selector: '[appLei]',
  providers: [{ provide: NG_VALIDATORS, useExisting: LeiChecksumValidator, multi: true }]
})
export class LeiChecksumValidator implements Validator, OnChanges {
  control?: AbstractControl;

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    if (control.value) {
      const v = control.value as string;
      const rv = validateLEI(v);
      if (rv === false) {
        return { lei: { failed: true } };
      }
    }
    return null;
  }

  ngOnChanges() {
    if (this.control)
      this.control.updateValueAndValidity();
  }
}

const validateLEI = (lei: string): boolean => {
  lei = lei.replace(/\s/g, '').toUpperCase();

  // Check if LEI is 20 characters long
  if (lei.length !== 20) {
    return false;
  }

  let m = 0;
  lei.split('').forEach(c => {
    if (c >= '0' && c <= '9') {
      m = (m * 10 + parseInt(c)) % 97;
    }
    else {
      m = (m * 100 + c.charCodeAt(0) - 55) % 97;
    }
  });
  //console.debug(m);
  return m === 1;
};


// custom validator to check that two fields match
export function MustMatch(controlName: string, matchingControlName: string): (formGroup: UntypedFormGroup) => void {
  return (formGroup: UntypedFormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (matchingControl.errors && !matchingControl.errors.mustMatch) {
      // return if another validator has already found an error on the matchingControl
      return;
    }

    // set error on matchingControl if validation fails
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
    } else {
      matchingControl.setErrors(null);
    }
  };
}
// Function for Template Driven Must Match
export function TemplateMustMatch(controlName: string, matchingControlName: string) {
  return (formGroup: UntypedFormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    // return null if controls haven't initialised yet
    if (!control || !matchingControl) {
      return null;
    }

    // return null if another validator has already found an error on the matchingControl
    if (matchingControl.errors && !matchingControl.errors.mustMatch) {
      return null;
    }

    // set error on matchingControl if validation fails
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
    } else {
      matchingControl.setErrors(null);
    }
    return null;
  };

}
export function ValidateChecksumReactive(controlName: string): (formGroup: UntypedFormGroup) => void {
  return (formGroup: UntypedFormGroup) => {
    const control = formGroup.controls[controlName];
    if (control.errors && !control.errors.checksumValidator) {
      // return if another validator has already found an error on the matchingControl
      return;
    }
    if (control.value) {
      const v = control.value.split('').map((c: string) => parseInt(c));
      const sum = 3*(v[0] + v[3] + v[6]) + 7*(v[1]+v[4]+v[7]) + v[2] + v[5] + v[8];
      if (sum % 10 === 0) {
        control.setErrors(null);
      }
      else {
        control.setErrors({ checksumValidator: true });
      }
    }
  };
}